import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react'; 

const BUENOS_AIRES_COORDS = { lat: -34.6037, lng: -58.3816 };

const LocationPickerMap = ({ initialCoordinates, onCoordinatesChange }) => {
  const [map, setMap] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [tempCoordinates, setTempCoordinates] = useState(initialCoordinates);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);

  useEffect(() => {
    // Ensure Google Maps API is loaded before proceeding
    if (window.google && !map) {
      const mapInstance = new window.google.maps.Map(document.getElementById('picker-map'), {
        center: initialCoordinates || BUENOS_AIRES_COORDS,
        zoom: 12,
        mapTypeId: 'hybrid', // Set default to satellite view
      });
      setMap(mapInstance);
      setAutocompleteService(new window.google.maps.places.AutocompleteService());
      setPlacesService(new window.google.maps.places.PlacesService(mapInstance));
    }
  }, [map, initialCoordinates]);

  useEffect(() => {
    if (map) {
      if (activeMarker) {
        activeMarker.setMap(null); // Clear previous marker
      }

      // Use tempCoordinates if available, otherwise initial, then default
      const markerPosition = tempCoordinates || initialCoordinates || BUENOS_AIRES_COORDS;
      const marker = new window.google.maps.Marker({
        position: markerPosition,
        map: map,
        draggable: true,
        animation: window.google.maps.Animation.DROP
      });

      marker.addListener('dragend', () => {
        const position = marker.getPosition();
        const newCoords = {
          lat: position.lat(),
          lng: position.lng()
        };
        setTempCoordinates(newCoords);
        onCoordinatesChange(newCoords); // Emit change to parent
      });

      setActiveMarker(marker);
      map.panTo(markerPosition);
      map.setZoom(16);
    } else if (activeMarker) {
      activeMarker.setMap(null);
      setActiveMarker(null);
    }
  }, [map, tempCoordinates, initialCoordinates, onCoordinatesChange]);

  const handleSearch = () => {
    if (!searchQuery || !autocompleteService) {
      setSearchResults([]);
      return;
    }

    const request = {
      input: searchQuery,
      location: new window.google.maps.LatLng(BUENOS_AIRES_COORDS), // Consider making this dynamic if your projects span wide areas
      radius: 50000, // Search radius in meters
    };

    autocompleteService.getPlacePredictions(request, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setSearchResults(predictions);
      } else {
        setSearchResults([]);
      }
    });
  };

  const selectSearchResult = (placeId) => {
    if (!placesService || !map) return;

    placesService.getDetails(
      {
        placeId: placeId,
        fields: ['geometry', 'formatted_address'] // Requesting geometry to get coordinates
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
          const coordinates = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
          setTempCoordinates(coordinates);
          onCoordinatesChange(coordinates); // Emit change
          map.panTo(coordinates);
          map.setZoom(15);
        }
      }
    );
    setSearchResults([]);
    setSearchQuery(''); // Clear search query after selection
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 bg-gray-50 border-b">
        <div className="relative mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              handleSearch(); // Trigger search on change
            }}
            placeholder="Buscar ubicación..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {searchResults.length > 0 && (
            <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
              {searchResults.map((result) => (
                <button
                  key={result.place_id}
                  onClick={() => selectSearchResult(result.place_id)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 border-b border-gray-100 last:border-0"
                >
                  {result.description}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-medium">Coordenadas seleccionadas: </span>
          {tempCoordinates &&
            typeof tempCoordinates.lat === 'number' &&
            typeof tempCoordinates.lng === 'number' ? (
            <span className="text-blue-600">
              {tempCoordinates.lat.toFixed(6)}, {tempCoordinates.lng.toFixed(6)}
            </span>
          ) : (
            <span className="text-gray-500">Mueve el marcador o busca una ubicación</span>
          )}
        </div>
      </div>
      <div id="picker-map" className="w-full h-full min-h-[400px]" /> {/* This div is where the map will be rendered */}
    </div>
  );
};

export default LocationPickerMap;